import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { PageHook } from 'app/components/common';
import { Divider } from '../index.js';

import './styles.less';

/**
 * This component can be used when needing to display a block of data
 * or information on the page. There are header and body child components
 * that can be rendered as children to this component that provide basic
 * styling.
 */
export const InfoBlock = ( props ) => {
    const classes = classnames(
        'info-block',
        { [ props.className ]: Boolean( props.className ) },
    );

    return (
        <div className={ classes } style={ { ...props.style } }>
            { props.children }
        </div>
    );
};

// The default prop values for the component.
InfoBlock.defaultProps = {
    className: undefined,
    style: undefined,
};

// The typedefs for the props of the component.
InfoBlock.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
};

/**
 * A header component for the InfoBlock component. Commonly this
 * component will be rendered above the body component all inside
 * of an InfoBlock component. This header component comes with
 * styling, a divider, and a page hook if an id is provided.
 */
InfoBlock.Header = ( props ) => {
    const classes = classnames(
        'info-block-header',
        { [ props.className ]: Boolean( props.className ) },
    );

    return (
        <div className={ classes } style={ { ...props.style } } onClick={ props.onClick }>
            <div className="info-block-header-content">
                { /* Render the main content for the header (children and potentially icon). */ }
                <PageHook iconVisibility={ props.hookIconVisibility } id={ props.id }>
                    <div className={ 'info-block-header-label' + ( props.animate ? ' animate' : '' ) }>
                        { Boolean( props.icon ) &&
                            <span className="info-block-header-icon">{ props.icon }</span>
                        }
                        { props.children }
                    </div>
                </PageHook>
            </div>

            { /* Show the header's divider only if told to do so through props. */ }
            { Boolean( props.divider ) &&
                <Divider />
            }
        </div>
    );
};

// The default prop values for the component.
InfoBlock.Header.defaultProps = {
    className: undefined,
    style: undefined,
    onClick: undefined,
    id: undefined,
    divider: true,
    icon: undefined,
    animate: false,
    hookIconVisibility: 'hidden',
};

// The typedefs for the props of the component.
InfoBlock.Header.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    onClick: PropTypes.func,
    id: PropTypes.string,
    divider: PropTypes.bool,
    icon: PropTypes.any,
    animate: PropTypes.bool,
    hookIconVisibility: PropTypes.oneOf( [ 'visible', 'hover', 'hidden' ] ),
};

/**
 * A body component for the InfoBlock component. Commonly this
 * component will be rendered below the header component all inside
 * of an InfoBlock component. This header component comes with
 * styling.
 */
InfoBlock.Body = ( props ) => {
    const classes = classnames(
        'info-block-body',
        { [ props.className ]: Boolean( props.className ) },
    );

    return (
        <div className={ classes } style={ { ...props.style } }>
            { props.children }
        </div>
    );
};

// The default prop values for the component.
InfoBlock.Body.defaultProps = {
    className: undefined,
    style: undefined,
};

// The typedefs for the props of the component.
InfoBlock.Body.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
};
